import { AM_INITIAL, DATE_FIELDS } from './pattern-token.constants';
import { isSlotBlank, isSlotFilled } from './slot-layout';
import {
  DATE_FIELD_COUNT,
  DECIMAL_BASE,
  FULL_YEAR_DIGITS,
  HOURS_PER_HALF_DAY,
  LAST_HOUR_OF_DAY,
  LAST_MINUTE_OF_HOUR,
  MAX_DAY_OF_MONTH,
  MONTHS_PER_YEAR,
  SHORT_YEAR_CENTURY_START,
  YEARS_PER_CENTURY,
} from './date-picker.constants';
import {
  ALL_SIDES,
  CompiledPattern,
  DayPeriod,
  EMPTY_FIELD_VALUES,
  FieldKind,
  FieldPart,
  FieldValues,
  ParseIssue,
  ParseResult,
  RangeSide,
  SideSelector,
} from './date-picker.model';

const EMPTY_RESULT: ParseResult = {
  empty: true,
  complete: false,
  values: EMPTY_FIELD_VALUES,
  date: null,
  issues: [],
};

export const daysInMonth = (year: number, month: number): number =>
  new Date(year, month, 0).getDate();

/** A year shorter than four digits belongs to this century: `26` -> `2026`, `1` -> `2001`. */
export const expandShortYear = (value: number): number =>
  value < YEARS_PER_CENTURY ? SHORT_YEAR_CENTURY_START + value : value;

export function hour12ToHour24(hour12: number, period: DayPeriod): number {
  if (period === DayPeriod.Am) return hour12 === HOURS_PER_HALF_DAY ? 0 : hour12;
  return hour12 === HOURS_PER_HALF_DAY ? HOURS_PER_HALF_DAY : hour12 + HOURS_PER_HALF_DAY;
}

export function hour24ToHour12(hour24: number): { hour12: number; dayPeriod: DayPeriod } {
  const dayPeriod: DayPeriod = hour24 < HOURS_PER_HALF_DAY ? DayPeriod.Am : DayPeriod.Pm;
  const hour12 =
    hour24 % HOURS_PER_HALF_DAY === 0 ? HOURS_PER_HALF_DAY : hour24 % HOURS_PER_HALF_DAY;
  return { hour12, dayPeriod };
}

/**
 * Keeps the two hour representations consistent. `hour24` is always derived, never
 * stored independently, so there is no second copy to fall out of sync.
 */
export function reconcileHours(values: FieldValues): FieldValues {
  if (values.hour12 !== null && values.dayPeriod !== null) {
    return { ...values, hour24: hour12ToHour24(values.hour12, values.dayPeriod) };
  }

  if (values.hour24 !== null) {
    const { hour12, dayPeriod } = hour24ToHour12(values.hour24);
    return { ...values, hour12, dayPeriod };
  }

  return values;
}

function readSlot(field: FieldPart, slots: readonly string[]): number | null {
  const raw = (slots[field.index] ?? '').trim();
  if (!raw) return null;
  const parsed = Number.parseInt(raw, DECIMAL_BASE);
  return Number.isNaN(parsed) ? null : parsed;
}

export function parseSlots(
  pattern: CompiledPattern,
  slots: readonly string[],
  side: SideSelector = ALL_SIDES,
): ParseResult {
  const fields = pattern.fields.filter((field) => side === ALL_SIDES || field.side === side);
  if (!fields.length) return EMPTY_RESULT;

  const relevant = fields.map((field) => slots[field.index] ?? '');
  if (relevant.every(isSlotBlank)) return EMPTY_RESULT;

  let values: FieldValues = { ...EMPTY_FIELD_VALUES };

  for (const field of fields) {
    switch (field.field) {
      case FieldKind.Year: {
        const raw = readSlot(field, slots);
        values.year = raw === null ? null : field.width === 2 ? expandShortYear(raw) : raw;
        break;
      }
      case FieldKind.Month:
        values.month = readSlot(field, slots);
        break;
      case FieldKind.Day:
        values.day = readSlot(field, slots);
        break;
      case FieldKind.Hour24:
        values.hour24 = readSlot(field, slots);
        break;
      case FieldKind.Hour12:
        values.hour12 = readSlot(field, slots);
        break;
      case FieldKind.Minute:
        values.minute = readSlot(field, slots);
        break;
      case FieldKind.DayPeriod: {
        const raw = (slots[field.index] ?? '').trim().toLowerCase();
        values.dayPeriod = raw === DayPeriod.Am || raw === DayPeriod.Pm ? raw : null;
        break;
      }
    }
  }

  values = reconcileHours(values);

  const complete = fields.every((field) => isSlotFilled(slots[field.index] ?? '', field.width));
  const issues: ParseIssue[] = [];
  if (!complete) issues.push(ParseIssue.Incomplete);

  // A date is only real once every one of its characters has been typed. Building one
  // from a half-entered year turns `2` into 1902 and `202` into 0202, which would be
  // emitted to the form on the way to `2026`.
  const dateFields = fields.filter(
    (field) =>
      field.field === FieldKind.Year ||
      field.field === FieldKind.Month ||
      field.field === FieldKind.Day,
  );
  const hasDate =
    dateFields.length === DATE_FIELD_COUNT &&
    dateFields.every((field) => isSlotFilled(slots[field.index] ?? '', field.width));

  let date: Date | null = null;

  if (hasDate && values.year !== null && values.month !== null && values.day !== null) {
    if (values.month < 1 || values.month > MONTHS_PER_YEAR) {
      issues.push(ParseIssue.OutOfRange);
    } else if (values.day > daysInMonth(values.year, values.month)) {
      issues.push(ParseIssue.InvalidDay);
    } else {
      date = new Date(
        values.year,
        values.month - 1,
        values.day,
        values.hour24 ?? 0,
        values.minute ?? 0,
        0,
        0,
      );
    }
  }

  return { empty: false, complete, values, date, issues };
}

export function parseRangeSlots(
  pattern: CompiledPattern,
  slots: readonly string[],
): { from: ParseResult; to: ParseResult } {
  return {
    from: parseSlots(pattern, slots, RangeSide.From),
    to: parseSlots(pattern, slots, RangeSide.To),
  };
}

/**
 * Numeric fields of one side of the pattern, split into its date half and its time half.
 * `dayPeriod` is not one of them: it is read out of the text, never out of a digit group.
 */
interface LooseFields {
  readonly all: readonly FieldPart[];
  readonly date: readonly FieldPart[];
  readonly time: readonly FieldPart[];
}

/** Which half of the pattern a pasted text is a reading of. */
interface LooseForm {
  readonly date: boolean;
  readonly time: boolean;
}

const DAY_PERIOD_PATTERN = /\b(a|p)\.?m?\.?\b/i;

function looseFields(pattern: CompiledPattern): LooseFields {
  const all = pattern.fields.filter(
    (field) => field.side !== RangeSide.To && field.field !== FieldKind.DayPeriod,
  );

  return {
    all,
    date: all.filter((field) => DATE_FIELDS.has(field.field)),
    time: all.filter((field) => !DATE_FIELDS.has(field.field)),
  };
}

function readDayPeriod(text: string): DayPeriod | null {
  const match = DAY_PERIOD_PATTERN.exec(text);
  if (!match) return null;
  return match[1]!.toLowerCase() === AM_INITIAL ? DayPeriod.Am : DayPeriod.Pm;
}

/**
 * How many groups arrived decides what they can be: the whole pattern, its date part or
 * its time part. Any other count is not a reading of this pattern, and half a date filled
 * in from `01/02` is worse than nothing.
 */
function looseForm(count: number, text: string, fields: LooseFields): LooseForm | null {
  const dateOnly = fields.date.length > 0 && count === fields.date.length;
  const timeOnly = fields.time.length > 0 && count === fields.time.length;

  if (fields.date.length > 0 && fields.time.length > 0 && count === fields.all.length) {
    return { date: true, time: true };
  }

  // Two groups can fit the clock and the first two date fields alike; a colon or an
  // `am` in the text is what says the reading was a time.
  if (dateOnly && timeOnly) {
    const time = text.includes(':') || DAY_PERIOD_PATTERN.test(text);
    return { date: !time, time };
  }

  if (dateOnly) return { date: true, time: false };
  if (timeOnly) return { date: false, time: true };

  return null;
}

/**
 * Tolerant parse for pasted text: digit groups in the order they were written, any
 * separators, leading zeros optional. Returns null when no valid reading exists.
 */
export function parseLoose(text: string, pattern: CompiledPattern): Partial<FieldValues> | null {
  if (!text) return null;

  const groups = text.match(/\d+/g);
  if (!groups?.length) return null;

  return parseGroups(groups, text, looseFields(pattern));
}

/**
 * Both ends of a range in one paste. Each half is read on its own (a text may well write
 * its two dates in different orders), so this only splits the groups and delegates.
 */
export function parseLooseRange(
  text: string,
  pattern: CompiledPattern,
): { from: Partial<FieldValues>; to: Partial<FieldValues> } | null {
  if (!text || !pattern.isRange) return null;

  const matches = [...text.matchAll(/\d+/g)];
  if (matches.length < 2 || matches.length % 2 !== 0) return null;

  const half = matches.length / 2;
  const cut = matches[half]!.index;
  const fields = looseFields(pattern);

  const from = parseGroups(
    matches.slice(0, half).map((match) => match[0]),
    text.slice(0, cut),
    fields,
  );
  const to = parseGroups(
    matches.slice(half).map((match) => match[0]),
    text.slice(cut),
    fields,
  );

  return from && to ? { from, to } : null;
}

function parseGroups(
  groups: readonly string[],
  text: string,
  fields: LooseFields,
): Partial<FieldValues> | null {
  const form = looseForm(groups.length, text, fields);
  if (!form) return null;

  const active = fields.all.filter((field) =>
    DATE_FIELDS.has(field.field) ? form.date : form.time,
  );

  const dateGroups: string[] = [];
  const timeGroups: string[] = [];
  const timeFields: FieldPart[] = [];

  active.forEach((field, index) => {
    if (DATE_FIELDS.has(field.field)) {
      dateGroups.push(groups[index]!);
      return;
    }
    timeFields.push(field);
    timeGroups.push(groups[index]!);
  });

  const date = form.date ? readDate(dateGroups, fields.date) : {};
  if (!date) return null;

  const values = { ...date, ...readTime(timeGroups, timeFields) };

  return normalizeLoose(applyClock(values, readDayPeriod(text)));
}

/**
 * Places the date groups. The four-digit group is the year — a two-digit one is not
 * supported, because `26` is indistinguishable from a day. Where the year sits then
 * decides the rest: leading means the text was year-month-day, since no locale writes
 * year-day-month.
 */
function readDate(
  groups: readonly string[],
  fields: readonly FieldPart[],
): Partial<FieldValues> | null {
  if (!fields.some((field) => field.field === FieldKind.Year)) return orderDayMonth(groups, fields);

  const yearAt = groups.findIndex((group) => group.length === FULL_YEAR_DIGITS);
  if (yearAt < 0 || groups.filter((group) => group.length === FULL_YEAR_DIGITS).length > 1)
    return null;

  const year = Number(groups[yearAt]);
  const rest = groups.filter((group, index) => index !== yearAt);
  const restFields = fields.filter((field) => field.field !== FieldKind.Year);

  if (yearAt === 0 && rest.length === 2 && restFields.length === 2) {
    return { year, month: Number(rest[0]), day: Number(rest[1]) };
  }

  const ordered = orderDayMonth(rest, restFields);

  return ordered && { ...ordered, year };
}

/**
 * Day and month with no year to anchor them. A group above 12 can only be the day; when
 * both readings survive, the target pattern's own order is the answer — it is the order
 * the user is looking at.
 */
function orderDayMonth(
  groups: readonly string[],
  fields: readonly FieldPart[],
): Partial<FieldValues> | null {
  const monthAt = fields.findIndex((field) => field.field === FieldKind.Month);
  const dayAt = fields.findIndex((field) => field.field === FieldKind.Day);

  if (groups.length !== 2 || monthAt < 0 || dayAt < 0) return positional(groups, fields);

  const first = Number(groups[0]);
  const second = Number(groups[1]);

  let monthFirst = monthAt < dayAt;
  if (first > MONTHS_PER_YEAR && second <= MONTHS_PER_YEAR) monthFirst = false;
  else if (second > MONTHS_PER_YEAR && first <= MONTHS_PER_YEAR) monthFirst = true;

  return monthFirst ? { month: first, day: second } : { day: first, month: second };
}

/** Fallback for date field sets that are not the usual day/month/year trio. */
function positional(groups: readonly string[], fields: readonly FieldPart[]): Partial<FieldValues> {
  const values: Partial<FieldValues> = {};

  fields.forEach((field, index) => {
    if (groups[index] === undefined) return;
    const value = Number(groups[index]);
    if (field.field === FieldKind.Year) values.year = value;
    else if (field.field === FieldKind.Month) values.month = value;
    else if (field.field === FieldKind.Day) values.day = value;
  });

  return values;
}

function readTime(groups: readonly string[], fields: readonly FieldPart[]): Partial<FieldValues> {
  const values: Partial<FieldValues> = {};

  fields.forEach((field, index) => {
    const value = Number(groups[index]);
    if (field.field === FieldKind.Hour24) values.hour24 = value;
    else if (field.field === FieldKind.Hour12) values.hour12 = value;
    else if (field.field === FieldKind.Minute) values.minute = value;
  });

  return values;
}

/**
 * The pasted text and the target pattern can disagree about the clock. An explicit `pm`
 * next to a 24-hour slot means the number was a 12-hour reading; a value above 12, or a
 * zero, landing in a 12-hour slot means the opposite. Whichever way, `hour24` stays the
 * derived value and is never stored independently.
 */
function applyClock(
  values: Partial<FieldValues>,
  dayPeriod: DayPeriod | null,
): Partial<FieldValues> {
  const next = { ...values };

  if (
    dayPeriod &&
    next.hour24 != null &&
    next.hour12 == null &&
    next.hour24 <= HOURS_PER_HALF_DAY
  ) {
    next.hour12 = next.hour24;
    next.hour24 = null;
  }

  // No 12-hour clock has an hour 0 any more than it has a 13: both readings are 24-hour.
  if (next.hour12 != null && (next.hour12 === 0 || next.hour12 > HOURS_PER_HALF_DAY)) {
    next.hour24 = next.hour12;
    next.hour12 = null;
  }

  if (dayPeriod) next.dayPeriod = dayPeriod;

  return next;
}

function normalizeLoose(values: Partial<FieldValues>): Partial<FieldValues> | null {
  const merged: FieldValues = { ...EMPTY_FIELD_VALUES, ...values };

  const invalid =
    (merged.month !== null && (merged.month < 1 || merged.month > MONTHS_PER_YEAR)) ||
    (merged.day !== null && (merged.day < 1 || merged.day > MAX_DAY_OF_MONTH)) ||
    (merged.hour24 !== null && merged.hour24 > LAST_HOUR_OF_DAY) ||
    (merged.hour12 !== null && (merged.hour12 < 1 || merged.hour12 > HOURS_PER_HALF_DAY)) ||
    (merged.minute !== null && merged.minute > LAST_MINUTE_OF_HOUR);

  if (invalid) return null;

  const result = reconcileHours(merged);
  const hasAnything = Object.values(result).some((value) => value !== null);

  return hasAnything ? result : null;
}
